import { createContext, useContext, useMemo } from 'react';

import { TreatmentTypeSetting, useTreatmentTypeSettings } from 'contexts/structure-edit/useTreatmentTypeSettings';
import { useConvertedProperty } from 'hooks';

interface PageStructureContextValue {
  layoutId: string | undefined;
  getTreatmentTypeSetting: (treatmentType: string) => TreatmentTypeSetting | undefined;
  treatmentTypeSettings: TreatmentTypeSetting[];
}

const DEFAULT_VALUE: PageStructureContextValue = {
  layoutId: undefined,
  getTreatmentTypeSetting: () => undefined,
  treatmentTypeSettings: []
};

export const PageStructureContext = createContext<PageStructureContextValue>(DEFAULT_VALUE);

interface PageStructureProviderProps {
  children: React.ReactNode;
  layoutId: string | undefined;
}

export const PageStructureProvider = ({ layoutId, children }: PageStructureProviderProps) => {
  const currentProperty = useConvertedProperty();
  const ttCatalog = useTreatmentTypeSettings(currentProperty ?? '');

  const getTreatmentTypeSetting = useMemo(
    () => (treatmentType: string) =>
      ttCatalog.find((t) => t.treatmentType === treatmentType || t.treatmentTypeKey === treatmentType),
    [ttCatalog]
  );

  const value = useMemo(
    () => ({ layoutId, getTreatmentTypeSetting, treatmentTypeSettings: ttCatalog }),
    [layoutId, getTreatmentTypeSetting, ttCatalog]
  );

  return <PageStructureContext.Provider value={value}>{children}</PageStructureContext.Provider>;
};

export const usePageStructureContext = () => useContext(PageStructureContext);
